// The compose page's recorder: `@bindery/speech/capture`'s machine and
// driver, wired so that a finished recording is handed to the page in the
// transition that produced it, not from an effect watching for it, because
// the child's sentence is submitted the moment they tap stop (no second tap,
// no POST from an effect). `useSpeechCapture` has no such callback, so this
// hook drives the machine itself; the live input-level ring is metered from
// the recorder's own stream (`onStream`), never from a second getUserMedia,
// which can mute the first capture on iOS Safari.
import {
  type CaptureEvent,
  type CaptureState,
  detectCapability,
  initialCaptureState,
  type Recording,
  type PermissionLikeError,
  type RejectedError,
  reduceCapture,
  startCapture,
  type CaptureDriver,
} from "@bindery/speech/capture";
import {
  DEFAULT_CAPTURE_LIMITS,
  type CaptureLimits,
} from "@bindery/speech/contract";
import { useEffect, useRef, useState } from "react";

const TICK_MS = 250;
const CAPTURING: ReadonlySet<CaptureState["tag"]> = new Set([
  "requesting-permission",
  "recording",
  "stopping",
]);
const LEVEL_STEP = 0.03;

export interface DictationCallbacks {
  readonly onRecorded: (recording: Recording) => void;
  readonly onRefused: (error: RejectedError) => void;
  readonly onBlocked: (error: PermissionLikeError) => void;
}

export interface Dictation {
  readonly state: CaptureState;
  /** Input level 0–1 while recording, 0 otherwise. */
  readonly level: number;
  readonly limits: CaptureLimits;
  /** From a tap handler only: may raise the permission prompt. */
  readonly start: () => void;
  readonly stop: () => void;
  /** Closes the microphone and keeps nothing: no recording is handed over. */
  readonly discard: () => void;
}

interface Meter {
  readonly close: () => void;
}

// The RMS of the time-domain signal, scaled so ordinary speech fills most of
// the ring and silence shows none.
const rms = (samples: Float32Array): number => {
  let sum = 0;
  for (const s of samples) sum += s * s;
  return Math.min(1, Math.sqrt(sum / samples.length) * 4);
};

// Reads the recorder's stream; the driver owns its tracks and stops them.
const openMeter = (
  stream: MediaStream,
  onLevel: (level: number) => void,
): Meter => {
  let closed = false;
  let frame = 0;
  // Safari 14 has only the prefixed constructor; the ring is a nicety there.
  if (typeof AudioContext === "undefined") return { close: () => undefined };
  const context = new AudioContext();
  // Autoplay policy may create it suspended; resuming is harmless.
  void context.resume().catch(() => undefined);
  const analyser = context.createAnalyser();
  analyser.fftSize = 1024;
  context.createMediaStreamSource(stream).connect(analyser);
  const samples = new Float32Array(analyser.fftSize);
  const read = () => {
    if (closed) return;
    analyser.getFloatTimeDomainData(samples);
    onLevel(rms(samples));
    frame = requestAnimationFrame(read);
  };
  read();
  return {
    close: () => {
      closed = true;
      cancelAnimationFrame(frame);
      void context.close().catch(() => undefined);
      onLevel(0);
    },
  };
};

export const useDictation = (callbacks: DictationCallbacks): Dictation => {
  const [capability] = useState(detectCapability);
  const [limits] = useState(() => DEFAULT_CAPTURE_LIMITS);
  const [state, setState] = useState(() => initialCaptureState(capability));
  const [level, setLevel] = useState(0);

  const stateRef = useRef(state);
  const driverRef = useRef<CaptureDriver | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const meterRef = useRef<Meter | null>(null);
  // The latest callbacks, for driver callbacks that outlive the render that
  // started the capture.
  const callbacksRef = useRef(callbacks);
  useEffect(() => {
    callbacksRef.current = callbacks;
  });

  const clearTick = (): void => {
    if (tickRef.current !== null) clearInterval(tickRef.current);
    tickRef.current = null;
  };
  const closeMeter = (): void => {
    meterRef.current?.close();
    meterRef.current = null;
  };
  const onLevel = (next: number): void =>
    setLevel((prev) =>
      Math.abs(prev - next) < LEVEL_STEP && next !== 0 ? prev : next,
    );

  const dispatch = (event: CaptureEvent): void => {
    const prev = stateRef.current;
    const next = reduceCapture(prev, event, limits);
    if (next === prev) return;
    stateRef.current = next;
    setState(next);

    if (
      next.tag === "requesting-permission" &&
      capability.tag === "supported"
    ) {
      driverRef.current = startCapture(capability.mime, {
        onStream: (stream) => {
          closeMeter();
          meterRef.current = openMeter(stream, onLevel);
        },
        onStarted: (at) => dispatch({ type: "permission-granted", at }),
        onStartFailed: (error) =>
          dispatch({ type: "permission-failed", error }),
        onStopped: (recording) =>
          dispatch({ type: "recorder-stopped", recording }),
        onFailed: () => dispatch({ type: "recorder-failed" }),
      });
    }
    if (next.tag === "recording" && prev.tag !== "recording") {
      tickRef.current = setInterval(
        () => dispatch({ type: "tick", at: performance.now() }),
        TICK_MS,
      );
    }
    if (next.tag !== "recording") {
      clearTick();
      closeMeter();
    }
    if (next.tag === "stopping") driverRef.current?.stop(next.endedBy);

    if (next.tag === "recorded") {
      driverRef.current = null;
      callbacksRef.current.onRecorded(next.recording);
      dispatch({ type: "redo-requested" });
    } else if (next.tag === "rejected") {
      driverRef.current = null;
      callbacksRef.current.onRefused(next.error);
      dispatch({ type: "redo-requested" });
    } else if (next.tag === "blocked") {
      driverRef.current = null;
      callbacksRef.current.onBlocked(next.error);
    }
  };

  useEffect(
    () => () => {
      clearTick();
      closeMeter();
      driverRef.current?.dispose();
      driverRef.current = null;
    },
    [],
  );

  // The capture machine has no way back from a capture in progress. A
  // disposed driver calls nothing afterwards, not even for a permission
  // prompt still open, so the hook may return the machine to idle itself.
  const discard = (): void => {
    if (!CAPTURING.has(stateRef.current.tag)) return;
    driverRef.current?.dispose();
    driverRef.current = null;
    clearTick();
    closeMeter();
    const idle = initialCaptureState(capability);
    stateRef.current = idle;
    setState(idle);
  };

  return {
    state,
    level,
    limits,
    start: () => dispatch({ type: "start-requested" }),
    stop: () => dispatch({ type: "stop-requested" }),
    discard,
  };
};
